import { UserStorage } from '@/services/user-storage';
import { env } from '../env';
import { authService } from '@/services/auth-service';
import { getAppInsightsSessionId } from '@/services/app-insights-service';

const BASE_URL = env.VITE_API_BASE_URL;

type ResponseType = 'json' | 'blob';

interface ApiRequestConfig extends RequestInit {
  url: string;
  params?: Record<string, unknown>;
  data?: unknown;
  responseType?: ResponseType;
}

const HTTP_PARSING_ERROR_NAME = 'HttpParsingError';

class HttpParsingError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = HTTP_PARSING_ERROR_NAME;
  }
}

export function isHttpParsingError(error: unknown): error is HttpParsingError {
  return error instanceof Error && error.name === HTTP_PARSING_ERROR_NAME;
}

async function handleResponse<T>(
  response: Response,
  responseType: ResponseType = 'json',
): Promise<T> {
  if (!response.ok) {
    let errorJson;

    try {
      errorJson = await response.json();
    } catch (parseError) {
      const errorMessage = `HTTP Error ${response.status}: ${response.statusText}`;
      /* eslint-disable-next-line no-console */
      console.error('Failed to parse error response:', parseError);
      throw new HttpParsingError(response.status, errorMessage);
    }

    if (response.status === 401 && errorJson?.name === 'TokenExpiredError') {
      authService.logout();
    }

    throw { ...errorJson, status: response.status };
  }

  // For 204 No Content, return undefined as there is no body
  if (response.status === 204) {
    return undefined as T;
  }

  switch (responseType) {
    case 'blob':
      return (await response.blob()) as T;
    default:
      return await response.json();
  }
}

function getHeaders(data?: unknown): HeadersInit {
  const headers: Record<string, string> = !(data instanceof FormData)
    ? {
        'Content-Type': 'application/json',
      }
    : {};

  const userStorage = new UserStorage();

  const token = userStorage.token;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const sessionId = getAppInsightsSessionId();
  if (sessionId) {
    headers['X-AI-Session-Id'] = sessionId;
  }

  return new Headers(headers);
}

function buildUrl(path: string, baseURL: string, params?: Record<string, unknown>): string {
  const url = new URL(path, baseURL);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        const valuesForKey = Array.isArray(value) ? value : [value];
        valuesForKey.forEach((v) => url.searchParams.append(key, v.toString()));
      }
    });
  }
  return url.toString();
}

export async function api<T>(config: ApiRequestConfig): Promise<T> {
  const { url, params, data, responseType, ...rest } = config;
  const fullUrl = buildUrl(url, BASE_URL, params);

  const response = await fetch(fullUrl, {
    ...rest,
    credentials: 'include',
    headers: getHeaders(data),
    body: data ? (data instanceof FormData ? data : JSON.stringify(data)) : undefined,
  });

  return handleResponse<T>(response, responseType);
}
